import { isExtension } from '../../../utils/environment';
import closestWith from '../../utils/closestWith';
import convertHexToRGB from './convertHexToRGB';
import getBackgroundColorRgbFromNode from './getBackgroundColorRgbFromNode';
import getColorContrast from './getColorContrast';

let defaultHighlightBackgroundRgb: [number, number, number] | undefined;

/*
  Before extension activation the CSS variable holds a non-hex value which convertHexToRGB
  can't parse; skip caching so a later call picks up the activated hex value.
*/
const getDefaultHighlightBackgroundRgb = (): typeof defaultHighlightBackgroundRgb => {
  if (!defaultHighlightBackgroundRgb) {
    const rgb = convertHexToRGB(
      window.getComputedStyle(document.documentElement).getPropertyValue('--highlight-background-color--normal').trim(),
    );
    if (rgb.every(Number.isFinite)) {
      defaultHighlightBackgroundRgb = rgb;
    }
  }
  return defaultHighlightBackgroundRgb;
};

// Check if contrast versus background colours is sufficient
export default function hasEnoughBackgroundColorContrast(element: HTMLElement): boolean {
  if (!isExtension) {
    return true;
  }

  const ancestors = [document.body, document.documentElement];
  const closestElementWithBackground = closestWith<HTMLElement>(element.parentNode, (node) =>
    Boolean(getBackgroundColorRgbFromNode(node)));
  if (closestElementWithBackground && !ancestors.includes(closestElementWithBackground)) {
    ancestors.push(closestElementWithBackground);
  }

  const highlightRgb = getDefaultHighlightBackgroundRgb();
  if (!highlightRgb) {
    return true;
  }

  return ancestors.every((ancestor) => {
    const rgb = getBackgroundColorRgbFromNode(ancestor);
    if (!rgb) {
      return true;
    }

    const contrast = getColorContrast(highlightRgb, rgb);

    // The default colour vs a white background is 1.07
    return (
      contrast >= 1 &&
      // Make sure it isn't too yellow. #FFF105 would pass otherwise
      typeof rgb[0] === 'number' &&
      typeof rgb[1] === 'number' &&
      typeof rgb[2] === 'number' &&
      !(rgb[0] > 150 && rgb[1] > 150 && rgb[2] < 50)
    );
  });
}
